/* eslint-disable react/prop-types */
/* eslint-disable react/destructuring-assignment */
import React from 'react'
import { cadastraEndereco, editaEndereco, deletaEndereco, listaEnderecos, cadastraEnderecoVigente } from '../api/endereco.servico'
import { useGlobal } from './GlobalContext'
import { isEmptyObject } from '../utils/helpers/helpers'

const EnderecoContext = React.createContext()

function EnderecoProvider( props ) {
  const [ state, setState ] = React.useState( !isEmptyObject( props.initialState )
    ? props.initialState
    : {
      'enderecoAdicionado': false,
      'enderecoEditado': false,
      'enderecoExcluido': false,
      'enderecoSelecionado': null,
      'enderecoVigente': {},
      'enderecosCadastrados': [],
      'loadingRequisicaoNovoEndereco': false
    } )

  const value = React.useMemo( () => {
    return {
      setState,
      state
    }
  }, [ state ] )

  return (
    <EnderecoContext.Provider
      value={ value }
      { ...props }
    />
  )
}

function useEndereco() {
  const context = React.useContext( EnderecoContext )
  const { setErroRequisicao } = useGlobal()

  if ( !context ) {
    throw new Error( 'useEndereco deve ser usado dentro de EnderecoProvider' )
  }

  const { state, setState } = context

  const setEnderecoSelecionado = enderecoSelecionado => {
    setState( ( prevState ) => ( {
      ...prevState,
      enderecoSelecionado
    } ) )
  }

  const setEnderecoAdicionado = enderecoAdicionado => {
    setState( ( prevState ) => ( {
      ...prevState,
      enderecoAdicionado
    } ) )
  }

  const setEnderecoExcluido = enderecoExcluido => {
    setState( ( prevState ) => ( {
      ...prevState,
      enderecoExcluido
    } ) )
  }

  const setEnderecoEditado = enderecoEditado => {
    setState( ( prevState ) => ( {
      ...prevState,
      enderecoEditado
    } ) )
  }

  const setEnderecoVigente = enderecoVigente => {
    setState( ( prevState ) => ( {
      ...prevState,
      enderecoVigente
    } ) )
  }

  const setListaEnderecosCadastrados = enderecosCadastrados => {
    setState( ( prevState ) => ( {
      ...prevState,
      enderecosCadastrados
    } ) )
  }

  const setLoadingRequisicaoNovoEndereco = loadingRequisicaoNovoEndereco => {
    setState( ( prevState ) => ( {
      ...prevState,
      loadingRequisicaoNovoEndereco
    } ) )
  }

  const getEnderecos = async idAssinatura => {
    setLoadingRequisicaoNovoEndereco( true )

    try {
      const response = await listaEnderecos()
      if ( response.data.length ) {
        setListaEnderecosCadastrados( response.data )
        /* eslint-disable array-callback-return */
        response.data.map( item => {
          if ( item.vigencia.assinaturas.includes( idAssinatura ) && !item.vigencia.pendente.includes( idAssinatura ) ) {
            setEnderecoVigente( item )
          }
        } )
      }
    } catch ( erro ) {
      setErroRequisicao( erro.response.status )
    } finally {
      setLoadingRequisicaoNovoEndereco( false )
    }
  }

  const postEndereco = async( endereco, idAssinatura ) => {
    setLoadingRequisicaoNovoEndereco( true )
    const { erros, frete, ...enderecoRecebido } = endereco

    const dadosEndereco = {
      'assinaturaId': idAssinatura,
      'endereco': enderecoRecebido
    }

    try {
      const enderecoCadastrado = await cadastraEndereco( dadosEndereco )
      if ( enderecoCadastrado ) {
        getEnderecos()
        setEnderecoSelecionado( enderecoCadastrado.data.endereco )
        setEnderecoAdicionado( true )
        return enderecoCadastrado.data.endereco
      }
    } catch ( erro ) {
      setErroRequisicao( erro.response.status )
    } finally {
      setLoadingRequisicaoNovoEndereco( false )
    }
  }

  const postEnderecoVigente = async( idEndereco, idAssinatura ) => {
    setLoadingRequisicaoNovoEndereco( true )

    const novoEndereco = {
      'assinatura_id': `${idAssinatura}`,
      'endereco': {
        'id': idEndereco
      }
    }

    try {
      await cadastraEnderecoVigente( novoEndereco )
      getEnderecos()
      setEnderecoSelecionado( null )
    } catch ( erro ) {
      setErroRequisicao( erro.response.status )
    } finally {
      setLoadingRequisicaoNovoEndereco( false )
    }
  }

  const editEndereco = async( endereco, idAssinatura ) => {
    setLoadingRequisicaoNovoEndereco( true )

    const { erros, frete, ...enderecoRecebido } = endereco

    const dadosEndereco = {
      'assinaturaId': idAssinatura,
      'endereco': enderecoRecebido
    }

    try {
      await editaEndereco( dadosEndereco )
      getEnderecos()
      setEnderecoEditado( true )
    } catch ( erro ) {
      setErroRequisicao( erro.response.status )
    } finally {
      setLoadingRequisicaoNovoEndereco( false )
    }
  }

  const deleteEndereco = async( enderecoId, idAssinatura ) => {
    setLoadingRequisicaoNovoEndereco( true )

    try {
      await deletaEndereco( enderecoId, idAssinatura )
      setEnderecoExcluido( true )
      const newListaEnderecosCadastrados = state.enderecosCadastrados.map( endereco => endereco.id === enderecoId ? '' : endereco ).filter( ax => ax )
      setListaEnderecosCadastrados( newListaEnderecosCadastrados )
    } catch ( erro ) {
      if ( erro.response.status ) {
        setErroRequisicao( erro.response.status, {
          'descricao': erro.response.data,
          'titulo': 'Erro ao excluir endereço'
        } )
      } else {
        setErroRequisicao( 500, {
          'descricao': 'Erro ao excluir endereço',
          'titulo': 'Erro interno'
        } )
      }
    } finally {
      setLoadingRequisicaoNovoEndereco( false )
    }
  }

  return {
    deleteEndereco,
    editEndereco,
    'enderecoAdicionado': state.enderecoAdicionado,
    'enderecoEditado': state.enderecoEditado,
    'enderecoExcluido': state.enderecoExcluido,
    'enderecoSelecionado': state.enderecoSelecionado,
    'enderecoVigente': state.enderecoVigente,
    'enderecosCadastrados': state.enderecosCadastrados,
    getEnderecos,
    'loadingRequisicaoNovoEndereco': state.loadingRequisicaoNovoEndereco,
    postEndereco,
    postEnderecoVigente,
    setEnderecoAdicionado,
    setEnderecoEditado,
    setEnderecoExcluido,
    setEnderecoSelecionado
  }
}

export { EnderecoProvider, useEndereco }
